/**
 * Admin Security page — passkey management.
 *
 * Feature folder: documentation/features/passkeys/
 * Plan doc:       documentation/features/passkeys/plan.md §5 (admin UI)
 *
 * Same behaviour as web's Profile_Settings_Passkeys but rendered in the
 * admin dark theme. Admins are expected to have at least one non-synced
 * passkey (e.g. platform authenticator) — the hint is shown in the copy.
 */

import {
  ActionIcon,
  Alert,
  Badge,
  Button,
  Card,
  Group,
  Stack,
  Text,
  TextInput,
  Title
} from '@mantine/core'
import { modals } from '@mantine/modals'
import { usePasskeys } from '@packages/supabase'
import { IconKey, IconPencil, IconPlus, IconTrash } from '@tabler/icons-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

export const Security = () => {
  const { t } = useTranslation()
  const {
    passkeys,
    loading,
    error,
    isSupported,
    registerPasskey,
    rename,
    remove
  } = usePasskeys()

  const [deviceName, setDeviceName] = useState('')

  const handleAdd = async () => {
    try {
      await registerPasskey({ deviceName: deviceName || undefined })
      setDeviceName('')
    } catch {
      // surfaced by hook
    }
  }

  const confirmRemove = (id: string, name: string | null) => {
    modals.openConfirmModal({
      title: t('auth.passkey.remove.title', { defaultValue: 'Remove passkey' }),
      children: (
        <Text size="sm">
          {t('auth.passkey.remove.confirm', {
            defaultValue:
              'Remove “{{name}}”? You can add another passkey any time.',
            name: name || t('auth.passkey.unnamed', { defaultValue: 'Passkey' })
          })}
        </Text>
      ),
      labels: {
        confirm: t('remove', { defaultValue: 'Remove' }),
        cancel: t('cancel', { defaultValue: 'Cancel' })
      },
      confirmProps: { color: 'red' },
      onConfirm: () => void remove(id)
    })
  }

  const promptRename = (id: string, current: string | null) => {
    const RenameContent = () => {
      const [v, setV] = useState(current ?? '')
      return (
        <Stack c="black">
          <TextInput
            label={t('auth.passkey.device_name.label', {
              defaultValue: 'Device name'
            })}
            value={v}
            onChange={(e) => setV(e.currentTarget.value)}
            data-testid="passkey-rename-input"
          />
          <Group justify="flex-end">
            <Button variant="default" onClick={() => modals.closeAll()}>
              {t('cancel', { defaultValue: 'Cancel' })}
            </Button>
            <Button
              onClick={async () => {
                await rename(id, v.trim())
                modals.closeAll()
              }}
              data-testid="passkey-rename-save"
            >
              {t('save', { defaultValue: 'Save' })}
            </Button>
          </Group>
        </Stack>
      )
    }
    modals.open({
      title: t('auth.passkey.rename.title', { defaultValue: 'Rename passkey' }),
      children: <RenameContent />
    })
  }

  return (
    <Stack c="white" gap="xl" p="md">
      <Group gap="xs">
        <IconKey size={22} />
        <Title order={2}>
          {t('auth.passkey.section.title', { defaultValue: 'Passkeys' })}
        </Title>
      </Group>

      {!isSupported ? (
        <Alert>
          {t('auth.passkey.unsupported', {
            defaultValue: 'Passkeys are not supported on this device or browser.'
          })}
        </Alert>
      ) : (
        <Card
          shadow="sm"
          padding="lg"
          radius="md"
          withBorder
          c="black"
          data-testid="passkey-manager"
        >
          <Stack gap="md">
            <Text size="sm" c="dimmed">
              {t('auth.passkey.section.description.admin', {
                defaultValue:
                  'Passkeys are used to sign in and to confirm sensitive admin actions (e.g. tenant deletion). We recommend registering at least one platform authenticator (Touch ID / Face ID / Windows Hello) AND one non-synced security key.'
              })}
            </Text>

            {passkeys.length === 0 && !loading && (
              <Text size="sm">
                {t('auth.passkey.empty', {
                  defaultValue: 'No passkeys registered yet.'
                })}
              </Text>
            )}

            <Stack gap="xs">
              {passkeys.map((pk) => (
                <Group key={pk.id} justify="space-between" wrap="nowrap">
                  <Stack gap={0}>
                    <Group gap="xs">
                      <Text fw={500}>
                        {pk.deviceName ||
                          t('auth.passkey.unnamed', {
                            defaultValue: 'Passkey'
                          })}
                      </Text>
                      {pk.backedUp && (
                        <Badge size="xs" variant="light">
                          {t('auth.passkey.synced', {
                            defaultValue: 'Synced'
                          })}
                        </Badge>
                      )}
                    </Group>
                    <Text size="xs" c="dimmed">
                      {t('auth.passkey.added_at', {
                        defaultValue: 'Added {{date}}',
                        date: new Date(pk.createdAt).toLocaleDateString()
                      })}
                    </Text>
                  </Stack>
                  <Group gap={4}>
                    <ActionIcon
                      variant="subtle"
                      onClick={() => promptRename(pk.id, pk.deviceName)}
                      aria-label="rename"
                      data-testid={`passkey-rename-${pk.id}`}
                    >
                      <IconPencil size={16} />
                    </ActionIcon>
                    <ActionIcon
                      variant="subtle"
                      color="red"
                      onClick={() => confirmRemove(pk.id, pk.deviceName)}
                      aria-label="remove"
                      data-testid={`passkey-remove-${pk.id}`}
                    >
                      <IconTrash size={16} />
                    </ActionIcon>
                  </Group>
                </Group>
              ))}
            </Stack>

            <TextInput
              label={t('auth.passkey.device_name.label', {
                defaultValue: 'Device name'
              })}
              value={deviceName}
              onChange={(e) => setDeviceName(e.currentTarget.value)}
              placeholder={t('auth.passkey.device_name.placeholder', {
                defaultValue: 'My device'
              })}
              data-testid="passkey-device-name-input"
            />
            <Group>
              <Button
                leftSection={<IconPlus size={16} />}
                loading={loading}
                onClick={handleAdd}
                data-testid="passkey-add-button"
              >
                {t('auth.passkey.add.label', { defaultValue: 'Add a passkey' })}
              </Button>
            </Group>

            {error && error.code !== 'canceled' && (
              <Alert color="red" data-testid="passkey-manager-error">
                {t(`auth.passkey.errors.${error.code}`, {
                  defaultValue: error.message
                })}
              </Alert>
            )}
          </Stack>
        </Card>
      )}
    </Stack>
  )
}

export default Security
